import { PileupRenderingModeEnum, TrackEnum } from "@selector/shared/openapi";
import { equals } from "ramda";
import { useCallback } from "react";
import type { Key } from "react-aria";
import {
  Card,
  Disclosure,
  HStack,
  Item,
  MenuOptionItem,
  MenuTriggerButton,
  ReorderableList,
  ReorderableListItem,
  Section,
  Switch,
  SymbolIcon,
  VStack,
} from "widgets";

import { useGenomeViewerStore } from "../../store/store.ts";
import { type Track, type TrackGroup } from "../../types.ts";
import { TrackGroupConfigurationDialog } from "./TrackGroupConfigurationDialog.tsx";
import style from "./TrackGroupsList.module.css";
import { TrackList, type TrackListTrack } from "./TracksList.tsx";

interface TrackGroupListItem {
  id: string;
  name: string; // need for reordable list
  trackIds: TrackGroup["trackIds"];
  organismName: TrackGroup["organismName"];
  contigName: TrackGroup["contigName"];
  isVisible: TrackGroup["graphics"]["isVisible"];
}

export const TrackGroupsListCard = () => {
  return (
    <Card noBorder title="Tracks" scrollable actions={[<OptionsMenu key={`options-menu`} />]}>
      <TrackGroupsList />
    </Card>
  );
};

export const TrackGroupsList = () => {
  const trackGroupsArray = useGenomeViewerStore<TrackGroupListItem[]>((state) => {
    const trackGroups = state.trackGroups;

    return Array.from(trackGroups.entries()).map(([groupId, trackGroup]) => ({
      id: groupId,
      name: trackGroup.contigName,
      trackIds: trackGroup.trackIds,
      organismName: trackGroup.organismName,
      contigName: trackGroup.contigName,
      isVisible: trackGroup.graphics.isVisible,
    }));
  }, equals);

  const changeTrackGroupPosition = useGenomeViewerStore((state) => state.changeTrackGroupPosition);

  const handleReorder = useCallback(
    (items: TrackGroupListItem[]) => {
      const trackGroups = useGenomeViewerStore.getState().trackGroups;
      const newTrackGroups: [string, TrackGroup][] = [];
      for (const { id } of items) {
        const trackGroup = trackGroups.get(id);
        if (trackGroup) {
          newTrackGroups.push([id, trackGroup]);
        }
      }

      changeTrackGroupPosition(new Map<string, TrackGroup>(newTrackGroups));
    },
    [changeTrackGroupPosition],
  );

  return (
    <ReorderableList<TrackGroupListItem>
      items={trackGroupsArray}
      onReorder={handleReorder}
      renderDragPreview={renderDragPreview}
      key={"reorderable-track-group-list"}
      dropIndicatorClassName={style.drop_indicator}
    >
      {({ id, name, trackIds, organismName, contigName, isVisible }) => (
        <ReordableItem
          id={id}
          name={name}
          trackIds={trackIds}
          organismName={organismName}
          contigName={contigName}
          isVisible={isVisible}
          key={`track-group-item-${id}`}
        />
      )}
    </ReorderableList>
  );
};

const renderDragPreview = (trackGroup: TrackGroupListItem) => (
  <div style={{ width: "100%", opacity: 0.9 }}>
    <VStack>
      <div className={style.title}>{trackGroup.organismName}</div>
      {!!trackGroup.contigName && <div className={style.subtitle}>{trackGroup.contigName}</div>}
    </VStack>
  </div>
);

const OptionsMenu = () => {
  const pileupRenderingMode = useGenomeViewerStore((state) => state.pileupRenderingMode);
  const setPileupRenderingMode = useGenomeViewerStore((state) => state.setPileupRenderingMode);
  const showTrackHeaders = useGenomeViewerStore((state) => state.showTrackHeaders);
  const toggleTrackHeaderVisibility = useGenomeViewerStore((state) => state.toggleTrackHeaderVisibility);
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);

  const hasProteinTracks = useGenomeViewerStore((state) =>
    [...state.tracks.values()].some((t) => t.trackType === TrackEnum.PROTEIN_LOCATION),
  );

  const handleAction = (key: Key) => {
    switch (key) {
      case "expanded-pileup": {
        setPileupRenderingMode(PileupRenderingModeEnum.EXPANDED);
        break;
      }
      case "condensed-pileup": {
        setPileupRenderingMode(PileupRenderingModeEnum.CONDENSED);
        break;
      }
      case "show-tracks-label": {
        toggleTrackHeaderVisibility();
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton icon="more_vert" label="More" onAction={handleAction}>
      <Section title="View">
        <Item key="show-tracks-label">
          <MenuOptionItem
            title={"Show Track Labels"}
            icon="label"
            tag={showTrackHeaders ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
      </Section>
      {hasProteinTracks ? (
        <Section title="Protein Tracks">
          <Item key="expanded-pileup">
            <MenuOptionItem
              title="Expanded"
              icon="expand"
              tag={pileupRenderingMode === PileupRenderingModeEnum.EXPANDED ? <SymbolIcon icon="check" /> : undefined}
            />
          </Item>
          <Item key="condensed-pileup">
            <MenuOptionItem
              title="Collapsed"
              icon="compress"
              tag={pileupRenderingMode === PileupRenderingModeEnum.CONDENSED ? <SymbolIcon icon="check" /> : undefined}
            />
          </Item>
        </Section>
      ) : null}
    </MenuTriggerButton>
  );
};

const ReordableItem = ({ id, trackIds, organismName, contigName, isVisible }: TrackGroupListItem) => {
  const setTrackGroupVisible = useGenomeViewerStore((state) => state.setTrackGroupVisible);
  const listedTracks = useGenomeViewerStore<TrackListTrack[]>(
    (state) =>
      trackIds
        .map((trackId) => state.tracks.get(trackId))
        .filter((track): track is Track => track != undefined)
        .map(({ trackId, trackType, subType, groupId, text, color, isVisible }) => ({
          trackId,
          trackType,
          subType,
          groupId,
          text,
          color,
          isVisible,
        })),
    equals,
  );

  return (
    <ReorderableListItem id={id} className={style.group_list_item} hideDragHandle>
      <VStack style={{ width: "100%" }} spacing={0}>
        <Disclosure
          headingClassName={style.item}
          header={
            <VStack spacing={1} style={{ overflow: "hidden" }}>
              <div className={style.title}>{organismName}</div>
              {!!contigName && <div className={style.subtitle}>{contigName}</div>}
            </VStack>
          }
          actions={[
            <HStack key={`track-group-action-${id}`}>
              <TrackGroupConfigurationDialog groupId={id} />
              <Switch
                isSelected={isVisible}
                onChange={(isSelected) => setTrackGroupVisible(id, isSelected)}
                aria-label={"Show track group"}
              />
            </HStack>,
          ]}
          data-e2e-label={"tracks"}
          defaultExpanded
          className={style.stack}
        >
          <TrackList groupId={id} tracks={listedTracks} key={`tracks-list-group-${id}`} />
        </Disclosure>
        <div className={style.line} />
      </VStack>
    </ReorderableListItem>
  );
};
